/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : LinkTestCasesDialog.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import {
  Autocomplete,
  Box,
  Checkbox,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import testCasesActions from "../../../redux/actions/testCasesActions";
import { HandleApiActions } from "../../../redux/actions";
import CustomDialogBoxV2 from "../../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";

function LinkTestCasesDialog({
  open,
  onClose,
  campaignDetails,
  setCampaignTestCasesList,
  onCampaignRefresh,
  isRecreationDialog = false,
}) {
  const dispatch = useDispatch();

  const testCasesList = useSelector(
    (state) => state.testCasesReducer.testCasesList,
  );

  const [campaignId, setCampaignId] = useState("");
  const safeCampaignDetails = campaignDetails || {};
  const campaignIdFromProps = safeCampaignDetails.campaign_id;
  const parentCampaignId = safeCampaignDetails.parent_campaign_id;
  const parentProjectId = safeCampaignDetails.parent_project_id;
  const parentVariantId = safeCampaignDetails.parent_variant_id;
  const projectId = safeCampaignDetails.project_id;
  const variantId = safeCampaignDetails.variant_id;
  const [testCasesOptionsList, setTestCasesOptionsList] = useState([]);
  const [selectedTestCaseIds, setSelectedTestCaseIds] = useState([]);
  const [campaignLinkedTestCasesIds, setCampaignLinkedTestCasesIds] = useState(
    [],
  );

  const getTestCaseIdentifiers = useCallback((testCase) => {
    return [
      testCase?.manual_test_case_id,
      testCase?.test_case_id,
      testCase?.value,
    ]
      .filter((id) => id !== undefined && id !== null && id !== "")
      .map(String);
  }, []);

  const isLinkTestCasesValid = () => {
    return selectedTestCaseIds?.length > 0;
  };

  const clearLinkTestCasesFields = () => {
    setTestCasesOptionsList([]);
    setSelectedTestCaseIds([]);
  };

  const handleLinkTestCases = async () => {
    const resp = await dispatch(
      HandleApiActions({
        ...testCasesActions.link_test_cases,
        params: {
          campaign_id: campaignId,
          test_case_ids: selectedTestCaseIds.map((tc) => tc.value),
        },
        show_toast: true,
      }),
    );
    if (!resp.error) {
      const respLTC = await dispatch(
        HandleApiActions({
          ...testCasesActions.get_campaign_test_cases,
          params: {
            campaign_id: campaignId,
          },
        }),
      );
      if (!respLTC.error) {
        setCampaignTestCasesList(respLTC?.data);
        setCampaignLinkedTestCasesIds(
          respLTC?.data?.flatMap(getTestCaseIdentifiers) || [],
        );
      }
      await onCampaignRefresh?.({ campaignId });
      onClose(false);
      clearLinkTestCasesFields();
    }
  };


  const handleGetParentCampaignLinkedTestCaseIds = useCallback(async () => {
    if (
      !campaignId ||
      !parentCampaignId ||
      !parentProjectId ||
      !parentVariantId
    ) {
      setTestCasesOptionsList([]);
      return;
    }

    const resp = await dispatch(
      HandleApiActions({
        ...testCasesActions.get_Parent_test_case_for_campaign,
        params: {
          campaign_id: campaignId,
          parent_campaign_id: parentCampaignId,
          parent_project_id: parentProjectId,
          parent_variant_id: parentVariantId,
          project_id: projectId,
          variant_id: variantId,
          page_number: 1,
          page_size: 500,
        },
      }),
    );
    if (!resp.error) {
      const filteredCampaignLinkedTestCasesIds = resp?.data?.map((tc) => ({
        value: tc?.test_case_id,
        label: tc?.name,
      }));
      setTestCasesOptionsList(filteredCampaignLinkedTestCasesIds);
    }
  }, [
    parentCampaignId,
    parentProjectId,
    parentVariantId,
    projectId,
    variantId,
    campaignId,
    dispatch,
  ]);

  const handleGetCampaignLinkedTestCaseIds = useCallback(async () => {
    if (!campaignId) {
      setCampaignLinkedTestCasesIds([]);
      return;
    }

    const resp = await dispatch(
      HandleApiActions({
        ...testCasesActions.get_campaign_test_cases,
        params: {
          campaign_id: campaignId,
          page_number: 1,
          page_size: 500,
        },
      }),
    );
    if (!resp.error) {
      setCampaignLinkedTestCasesIds(
        resp?.data?.flatMap(getTestCaseIdentifiers) || [],
      );
    }
  }, [campaignId, dispatch, getTestCaseIdentifiers]);

  useEffect(() => {
    if (open && !isRecreationDialog) {
      dispatch(
        HandleApiActions({
          ...testCasesActions.get_test_cases,
          params: {
            project_id: Number(projectId),
            variant_id: Number(variantId),
            page_number: 1,
            page_size: 500,
          },
        }),
      );
      handleGetCampaignLinkedTestCaseIds();
    } else if (isRecreationDialog && open) {
      handleGetParentCampaignLinkedTestCaseIds();
    }
  }, [
    projectId,
    variantId,
    dispatch,
    handleGetParentCampaignLinkedTestCaseIds,
    handleGetCampaignLinkedTestCaseIds,
    isRecreationDialog,
    open,
  ]);

  useEffect(() => {
    setCampaignId(campaignIdFromProps || "");
  }, [campaignIdFromProps]);

  useEffect(() => {
    handleGetCampaignLinkedTestCaseIds();
  }, [handleGetCampaignLinkedTestCaseIds]);

  useEffect(() => {
    if (!isRecreationDialog) {
      const filteredTestCasesOptionsList = testCasesList
        ?.filter(
          (ltc) =>
            !getTestCaseIdentifiers(ltc).some((id) =>
              campaignLinkedTestCasesIds.includes(id),
            ),
        )
        ?.map((tc) => ({
          value: tc?.test_case_id,
          label: tc?.name,
        }));
      setTestCasesOptionsList(filteredTestCasesOptionsList);
    }
  }, [
    campaignLinkedTestCasesIds,
    getTestCaseIdentifiers,
    isRecreationDialog,
    testCasesList,
  ]);

  return (
    <CustomDialogBoxV2
      open={open}
      onClose={() => {
        onClose(false);
        clearLinkTestCasesFields();
      }}
      onClickReject={() => {
        onClose(false);
        clearLinkTestCasesFields();
      }}
      title={"Add Test Cases"}
      onClickSuccess={() => {
        handleLinkTestCases();
      }}
      onDisabled={!isLinkTestCasesValid()}
      successButtonName={"Add"}
      rejectButtonName={"Close"}
      actionButtonStyle={"continuous"}
    >
      <Stack spacing={2} sx={{ width: "350px", height: "160px" }}>
        <Box>
          <Box sx={{ mb: 1 }}>
            <Typography sx={{ fontSize: "14px", color: "#666" }}>
              Search / Select Test Cases{" "}
              <Box component="span" sx={{ color: "red" }}>
                *
              </Box>
            </Typography>
          </Box>
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={testCasesOptionsList}
            value={selectedTestCaseIds}
            onChange={(event, newValue) => {
              setSelectedTestCaseIds(newValue);
            }}
            getOptionLabel={(option) => option?.label || ""}
            isOptionEqualToValue={(option, value) =>
              option?.value === value?.value
            }
            renderOption={(props, option, { selected }) => (
              <Box
                component="li"
                {...props}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  p: "8px 12px !important",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                  "& .MuiAutocomplete-listbox": {
                    maxHeight: 250,
                    overflowY: "auto",
                    scrollbarColor: "#888 #f1f1f1",
                    scrollbarWidth: "thin",
                    "&::-webkit-scrollbar": { width: "8px", height: "8px" },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "#f1f1f1",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#888",
                      borderRadius: "4px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      backgroundColor: "#555",
                    },
                  },
                }}
              >
                <Checkbox
                  checked={selected}
                  sx={{
                    marginRight: 1,
                    color: "#000",
                    "&.Mui-checked": {
                      color: "#000",
                    },
                  }}
                />
                {option?.label}
              </Box>
            )}
            renderTags={() => null}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: 32,
                    borderRadius: 0,
                    "&:hover fieldset": {
                      borderColor: "#000000",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#000000",
                    },
                  },
                }}
              />
            )}
            slotProps={{
              paper: {
                sx: {
                  maxHeight: 250,
                  overflow: "hidden",
                  "& .MuiAutocomplete-listbox": {
                    maxHeight: 250,
                    overflowY: "auto",
                    scrollbarColor: "#888 #f1f1f1",
                    scrollbarWidth: "thin",
                    "&::-webkit-scrollbar": { width: "8px", height: "8px" },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "#f1f1f1",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#888",
                      borderRadius: "4px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      backgroundColor: "#555",
                    },
                  },
                },
              },
            }}
          />
          <Box
            sx={{
              maxHeight: "95px",
              width: "100%",
              overflow: "auto",
              px: 0.5,
              pt: 0.5,
              mb: 2,
              ...(selectedTestCaseIds?.length > 0 && {
                border: "1px solid lightgrey",
              }),
            }}
          >
            {selectedTestCaseIds?.map((testcase) => (
              <Box
                key={testcase?.value}
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  mb: 0.5,
                  mr: 0.5,
                  backgroundColor: "#232323",
                  color: "white",
                  borderRadius: 0,
                  padding: "4px 8px",
                  fontSize: "12px",
                  fontWeight: 500,
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "#1a1a1a",
                  },
                }}
                onClick={() => {
                  setSelectedTestCaseIds(
                    selectedTestCaseIds.filter(
                      (c) => c?.value !== testcase?.value,
                    ),
                  );
                }}
              >
                {testcase?.label}
                <span
                  style={{
                    marginLeft: "8px",
                    fontWeight: "bold",
                    cursor: "pointer",
                  }}
                >
                  ×
                </span>
              </Box>
            ))}
          </Box>
        </Box>
      </Stack>
    </CustomDialogBoxV2>
  );
}

LinkTestCasesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  campaignDetails: PropTypes.exact({
    campaign_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    project_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    variant_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    parent_campaign_id: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
    parent_project_id: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
    parent_variant_id: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
  }),
  setCampaignTestCasesList: PropTypes.func.isRequired,
  onCampaignRefresh: PropTypes.func,
  isRecreationDialog: PropTypes.bool,
};

export default LinkTestCasesDialog;
